// Dashboard TypeScript interfaces matching backend DTOs

import { DashboardMetricKey, MetricTrend, StudioType } from "../common/enums";

export interface MetricData {
  value: string;
  trend: MetricTrend;
  comparison: number | null;
}

export interface DashboardMetrics {
  cancelledByUser: MetricData | null;
  cancelledByAdmin: MetricData | null;
  userReservations: MetricData | null;
  adminReservations: MetricData | null;
  usersOnWaitingList: MetricData | null;
  noShowReservations: MetricData | null;
  unconfirmedReservations: MetricData | null;
  confirmedRevenue: MetricData | null;
  eventsCount: MetricData | null;
  newUserSignups: MetricData | null;
  noShowRate: MetricData | null;
  classCapacityUtilization: MetricData | null;
  revenueFromPlans: MetricData | null;
  staffCommission: MetricData | null;
  newVsReturningUsersRatio: MetricData | null;
  averageClassSize: MetricData | null;
  classesTaught: MetricData | null;
  peakHours: MetricData | null;
  mostPopularClasses: MetricData | null;
  totalActiveUsers: MetricData | null;
  expiringPlans: MetricData | null;
  lowAttendanceClasses: MetricData | null;
  storeTurnover: MetricData | null;
  storeSalesCount: MetricData | null;
  storeGrossProfit: MetricData | null;
  storeAverageOrderValue: MetricData | null;
  storeTopProducts: MetricData | null;
  storeLowStock: MetricData | null;
  storeExpiringProducts: MetricData | null;
}

export interface DetailItem {
  name: string;
  dateTime?: Date;
  value?: string;
  id?: number;
  itemType?: string;
}

export interface MetricDetail {
  items: DetailItem[];
}

export interface MetricConfig {
  key: DashboardMetricKey;
  title: string;
  icon: string;
  iconClass: string;
  description: string;
  hasDetail: boolean;
  minRole?: number;
  /**
   * Studio types this metric is relevant for. The metric is shown only if the
   * selected studio has at least one matching type. Omit for metrics that apply
   * to every studio (e.g. new user signups).
   */
  studioTypes?: StudioType[];
  /**
   * The value is free text (e.g. a product name) rather than a number, so it is
   * rendered in a smaller wrapping style and the period-over-period comparison
   * line is hidden.
   */
  isTextValue?: boolean;
}

// Constants for type safety
export const ALL_METRIC_KEYS = Object.values(DashboardMetricKey).filter(
  (value) => typeof value === "number"
) as DashboardMetricKey[];

export const BASIC_METRICS_KEYS = [
  DashboardMetricKey.CancelledByUser,
  DashboardMetricKey.CancelledByAdmin,
  DashboardMetricKey.UserReservations,
  DashboardMetricKey.AdminReservations,
  DashboardMetricKey.UsersOnWaitingList,
  DashboardMetricKey.NoShowReservations,
  DashboardMetricKey.UnconfirmedReservations,
  DashboardMetricKey.ConfirmedRevenue,
  DashboardMetricKey.EventsCount,
  DashboardMetricKey.NewUserSignups,
  DashboardMetricKey.NoShowRate,
  DashboardMetricKey.ClassCapacityUtilization,
];

export const ANALYTICS_METRICS_KEYS = [
  DashboardMetricKey.RevenueFromPlans,
  DashboardMetricKey.StaffCommission,
  DashboardMetricKey.NewVsReturningUsersRatio,
  DashboardMetricKey.AverageClassSize,
  DashboardMetricKey.ClassesTaught,
  DashboardMetricKey.PeakHours,
  DashboardMetricKey.MostPopularClasses,
  DashboardMetricKey.TotalActiveUsers,
  DashboardMetricKey.ExpiringPlans,
  DashboardMetricKey.LowAttendanceClasses,
];

export const STORE_METRICS_KEYS = [
  DashboardMetricKey.StoreTurnover,
  DashboardMetricKey.StoreSalesCount,
  DashboardMetricKey.StoreGrossProfit,
  DashboardMetricKey.StoreAverageOrderValue,
  DashboardMetricKey.StoreTopProducts,
  DashboardMetricKey.StoreLowStock,
  DashboardMetricKey.StoreExpiringProducts,
];
